
import { BuilderContext, kickHeights, largeScoreTimes, scores, scoreTimes } from "@Context/game/builder/builder-context";
import { ScenarioActionType } from '@Enums/index';
import { fa100, faArrowsAltV, faClock, faMinusCircle, faXmark } from "@fortawesome/pro-solid-svg-icons";
import useI18n from "@i18n";
import { Typography, useTheme } from '@mui/material';
import '@pixi/events';
import { Menu, MenuItem, SubMenu } from "@spaceymonk/react-radial-menu";
import { clamp, isNumber } from 'lodash-es';
import { useContext, useEffect, useState } from "react";
import { getBestHeightForCategory, getCategoryForHeight, getProjectileFlightTime, KickHeight } from './functions/physics';
import { getSelectedPlayer, updateAction, updatePlayer } from './functions/player-functions';
import { isValidIndex } from './functions/utilities';
import { PhysicsPrompt, Player, PlayerAction } from './models/builder-data-types';
import RadialItem from './radial-item';

const ActionMenu = () => {

  const { state, dispatch } = useContext(BuilderContext);
  const { i18n } = useI18n();
  const theme = useTheme();
  const [selectedPlayer, setSelectedPlayer] = useState<Player | undefined>();
  const [selectedAction, setSelectedAction] = useState<PlayerAction | undefined>();
  const [menuProps, setMenuProps] = useState({
    centerX: state.radialAnchor.x,
    centerY: state.radialAnchor.y,
    innerRadius: 50,
    outerRadius: 160,
    show: state.showActionRadial,
    animation: ["fade" as const, "scale" as const],
    animationTimeout: 120,
    theme: 'dark' as 'dark',
    drawBackground: true
  });

  useEffect(() => {
    setMenuProps({
      centerX: state.radialAnchor.x,
      centerY: state.radialAnchor.y,
      innerRadius: 50,
      outerRadius: 160,
      show: state.showActionRadial,
      animation: ["fade", "scale"],
      animationTimeout: 120,
      theme: 'dark',
      drawBackground: true
    });
  }, [state.radialAnchor, state.showActionRadial]);

  useEffect(() => {
    setSelectedPlayer(getSelectedPlayer(state));
  }, [state.selectedPlayer, state.clip.scenario.players]);

  useEffect(() => {
    setSelectedAction(selectedPlayer?.actions[state.selectedAction]);
  }, [selectedPlayer, state.selectedAction]);

  const handleActionDelete = () => {
    const selectedPlayer = getSelectedPlayer(state);
    if (!selectedPlayer || !isValidIndex(state.selectedAction, selectedPlayer.actions)) return;
    console.log('Deleting action');

    const newPlayer: Player = { ...selectedPlayer, actions: [] };
    selectedPlayer.actions.forEach((action, index) => {
      if (index !== state.selectedAction) {
        newPlayer.actions.push({ ...action });
      }
    });

    updatePlayer(state, dispatch, newPlayer);
    dispatch({ showActionRadial: false });
  };

  const handleDeselectClick = () => {
    dispatch({ selectedAction: -1, showActionRadial: false });
  };

  const applyHeight = (player: Player, actionIndex: number, newHeight: number, newTime?: number) => {
    const newPlayer: Player = { ...player, actions: [] };
    player.actions.forEach((action, index) => {
      if (index === actionIndex) {
        const updated = { ...action, height: newHeight };
        if (newTime !== undefined) {
          updated.time = newTime;
          updated.endTime = Math.max(updated.endTime, newTime);
        }
        newPlayer.actions.push(updated);
      } else {
        newPlayer.actions.push({ ...action });
      }
    });
    updatePlayer(state, dispatch, newPlayer);
  };

  const handleHeightClick = (category: KickHeight) => {
    const player = getSelectedPlayer(state);
    if (!player || !isValidIndex(state.selectedAction, player.actions)) return;

    const action = player.actions[state.selectedAction];
    const currentFlightTime = action.time - action.startTime;

    if (category.height <= 0) {
      applyHeight(player, state.selectedAction, 0);
      dispatch({ showActionRadial: false });
      return;
    }

    const result = getBestHeightForCategory(category, currentFlightTime);

    if (!result.needsTimeAdjust) {
      applyHeight(player, state.selectedAction, result.height);
      dispatch({ showActionRadial: false });
      return;
    }

    const clampedHeight = result.clampedTo!;
    const requiredFlightTime = getProjectileFlightTime(clampedHeight)!;
    const currentCategory = getCategoryForHeight(action.height);

    const prompt: PhysicsPrompt = {
      mode: "height-changed",
      playerId: player.id,
      actionIndex: state.selectedAction,
      currentHeight: action.height,
      currentFlightTime,
      proposedHeight: clampedHeight,
      proposedFlightTime: requiredFlightTime,
      proposedHeightName: category.name,
      currentHeightName: currentCategory.name,
      onConfirm: () => {
        const freshPlayer = getSelectedPlayer(state);
        if (!freshPlayer) return;
        const newTime = freshPlayer.actions[state.selectedAction].startTime + requiredFlightTime;
        applyHeight(freshPlayer, state.selectedAction, clampedHeight, newTime);
      },
      onCancel: () => {},
    };

    dispatch({ physicsPrompt: prompt, showActionRadial: false });
  };

  const handleScoreClick = (score: number) => {
    const selectedPlayer = getSelectedPlayer(state);
    if (!selectedPlayer || !isNumber(score) || !selectedAction) return;
    const newAction: PlayerAction = { ...selectedAction, index: clamp(score, 0, 100) };
    updateAction(state, dispatch, selectedPlayer.id, state.selectedAction, newAction);
    dispatch({ showActionRadial: false });
  };

  const handleScoreTimeClick = (time: number) => {
    const selectedPlayer = getSelectedPlayer(state);
    if (!selectedPlayer || !isNumber(time) || !selectedAction) return;
    const newAction: PlayerAction = { ...selectedAction, time: time };
    updateAction(state, dispatch, selectedPlayer.id, state.selectedAction, newAction);
    console.log('Setting time', time, 'for action', newAction);
    dispatch({ showActionRadial: false });
  }

  if (selectedAction === undefined) return null;

  if (selectedAction.type === ScenarioActionType.PassTarget || selectedAction.type === ScenarioActionType.ShootDirection) {
    return (
      <Menu {...menuProps}>
        <MenuItem onItemClick={handleDeselectClick} data="">
          <RadialItem icon={faXmark} text={i18n("builder.action.deselect")} />
        </MenuItem>
        <MenuItem onItemClick={handleActionDelete} data="">
          <RadialItem icon={faMinusCircle} text={i18n("builder.action.delete")} colour={theme.palette.dsDestructive.v200} />
        </MenuItem>
        <SubMenu
          itemView={<>
            <RadialItem icon={faArrowsAltV} text={i18n("builder.action.set.height")} />
          </>}
          data="2. Sub Menu"
          displayPosition="center"
        >
          {kickHeights.map((height) => (
            <MenuItem onItemClick={() => handleHeightClick(height)} data="2.1. Item">
              <Typography>{height.name}</Typography>
            </MenuItem>
          ))}
        </SubMenu>
      </Menu>
    );
  }

  if (selectedAction.type === ScenarioActionType.Score) {
    return (
      <Menu {...menuProps}>
        <MenuItem onItemClick={handleDeselectClick} data="">
          <RadialItem icon={faXmark} text={i18n("builder.action.deselect")} />
        </MenuItem>
        <MenuItem onItemClick={handleActionDelete} data="">
          <RadialItem icon={faMinusCircle} text={i18n("builder.action.delete")} colour={theme.palette.dsDestructive.v200} />
        </MenuItem>
        <SubMenu
          itemView={<>
            <RadialItem icon={fa100} text={i18n("builder.action.set.score")} />
          </>}
          data="2. Sub Menu"
          displayPosition="center"
        >
          {scores.map((score) => (
            <MenuItem onItemClick={() => handleScoreClick(score)} data="2.1. Item">
              <Typography
                color={selectedAction.index === score ? theme.palette.dsSecondaryYellow.v300 : undefined}
                sx={{ fontWeight: selectedAction.index === score ? 800 : 200 }}
              >
                {score}
              </Typography>
            </MenuItem>
          ))}
        </SubMenu>
        <SubMenu
          itemView={<>
            <RadialItem icon={faClock} text={i18n("builder.action.set.time")} />
          </>}
          data="2. Sub Menu"
          displayPosition="center"
        >
          {scoreTimes.map((time) => (
            <MenuItem onItemClick={() => handleScoreTimeClick(time)} data="2.1. Item">
              <Typography
                color={selectedAction.time === time ? theme.palette.dsSecondaryYellow.v300 : undefined}
                sx={{ fontWeight: selectedAction.time === time ? 800 : 200 }}
              >
                {time}s
              </Typography>
            </MenuItem>
          ))}
          <SubMenu
            itemView={<>
              <RadialItem icon={faClock} text={i18n("builder.action.more")} />
            </>}
            data="2. Sub Menu"
            displayPosition="center"
          >
            {largeScoreTimes.map((time) => (
              <MenuItem onItemClick={() => handleScoreTimeClick(time)} data="2.1. Item">
                <Typography
                  color={selectedAction.time === time ? theme.palette.dsSecondaryYellow.v300 : undefined}
                  sx={{ fontWeight: selectedAction.time === time ? 800 : 200 }}
                >
                  {time}s
                </Typography>
              </MenuItem>
            ))}
          </SubMenu>
        </SubMenu>
      </Menu>
    );
  }

  return (
    <Menu {...menuProps}>
      <MenuItem onItemClick={handleDeselectClick} data="">
        <RadialItem icon={faXmark} text={i18n("builder.action.deselect")} />
      </MenuItem>
      <MenuItem onItemClick={handleActionDelete} data="">
        <RadialItem icon={faMinusCircle} text={i18n("builder.action.delete")} colour={theme.palette.dsDestructive.v200} />
      </MenuItem>
    </Menu>
  )
}

export default ActionMenu;